import { useEffect, useRef } from 'react';

/**
 * Custom hook to enable drag-to-scroll functionality on an element
 * @param {string} selector - CSS selector for the element to make draggable
 * @returns {Object} - Object containing the ref to attach to the container
 */
const useDragScroll = (selector) => {
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Find the scrollable element using the selector
    const scrollElement = container.querySelector(selector);
    if (!scrollElement) return;

    let isDown = false;
    let startX;
    let scrollLeft;
    let hasDragged = false;

    const handleMouseDown = (e) => {
      isDown = true;
      hasDragged = false;
      scrollElement.style.cursor = 'grabbing';
      scrollElement.style.userSelect = 'none';
      startX = e.pageX - scrollElement.offsetLeft;
      scrollLeft = scrollElement.scrollLeft;
    };

    const handleMouseLeave = () => {
      isDown = false;
      scrollElement.style.cursor = 'grab';
      scrollElement.style.userSelect = 'auto';
    };

    const handleMouseUp = () => {
      isDown = false;
      scrollElement.style.cursor = 'grab';
      scrollElement.style.userSelect = 'auto';
    };

    const handleMouseMove = (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - scrollElement.offsetLeft;
      const walk = (x - startX) * 2; // Multiply by 2 for faster scroll

      // If moved more than 5 pixels, consider it a drag
      if (Math.abs(walk) > 5) {
        hasDragged = true;
      }

      scrollElement.scrollLeft = scrollLeft - walk;
    };

    const handleClick = (e) => {
      // Prevent thumbnail click if user dragged
      if (hasDragged) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    // Set initial cursor
    scrollElement.style.cursor = 'grab';

    // Add event listeners
    scrollElement.addEventListener('mousedown', handleMouseDown);
    scrollElement.addEventListener('mouseleave', handleMouseLeave);
    scrollElement.addEventListener('mouseup', handleMouseUp);
    scrollElement.addEventListener('mousemove', handleMouseMove);
    scrollElement.addEventListener('click', handleClick, true);

    // Cleanup
    return () => {
      scrollElement.removeEventListener('mousedown', handleMouseDown);
      scrollElement.removeEventListener('mouseleave', handleMouseLeave);
      scrollElement.removeEventListener('mouseup', handleMouseUp);
      scrollElement.removeEventListener('mousemove', handleMouseMove);
      scrollElement.removeEventListener('click', handleClick, true);
    };
  }, [selector]);

  return containerRef;
};

export default useDragScroll;

